import { useEffect, type ReactNode } from "react";
import { NavLink } from "react-router";
import { LegalFooter } from "../legal/LegalFooter";
import { CheckoutHost } from "./checkout/CheckoutHost";
import { PlacementHost } from "./advertising/PlacementDialog";
import { StoreSetupHost } from "./storeSetup/StoreSetupDialog";
import { OfferSetupHost } from "./rewards/offers/OfferSetupDialog";
import { reviewTabsFor } from "./admin/staffLinks";
import { useStaffAccess } from "./admin/useStaffAccess";
import { StoreThemeHost } from "./StoreThemeHost";
import { TaskHost } from "./rewards/AttemptDialog";
import { Toasts } from "./Toasts";

/**
 * Plain HTML page frame for the directory, account, statistics, order and demo pages. It hosts the
 * same dollar checkout and commerce notices as the town, so every purchase works without WebGL.
 */
export function PageShell({ title, children, narrow = false }: { title: string; children: ReactNode; narrow?: boolean }) {
  // Staff links follow the permission matrix (ADR-0035): Review for any /review tab (moderators and admins review;
  // staff manage Townsy house campaigns on the same page), Staff tools for any staff permission. Cosmetic only.
  const staff = useStaffAccess();
  const reviewer = reviewTabsFor(staff.permissions).length > 0;
  useEffect(() => {
    document.title = `${title} · Townsy`;
    return () => {
      document.title = "Townsy";
    };
  }, [title]);
  return (
    <div className="page">
      <header className="page-header">
        <NavLink to="/" className="wordmark">
          Townsy
        </NavLink>
        <nav aria-label="Primary">
          <NavLink to="/" end>
            Town
          </NavLink>
          <NavLink to="/directory">Directory</NavLink>
          <NavLink to="/stats">Statistics</NavLink>
          <NavLink to="/earn">Earn coins</NavLink>
          <NavLink to="/advertise">Advertise</NavLink>
          <NavLink to="/orders">Orders</NavLink>
          <NavLink to="/account">Account</NavLink>
          {reviewer ? <NavLink to="/review">Review</NavLink> : null}
          {staff.access === "staff" ? <NavLink to="/admin">Staff</NavLink> : null}
        </nav>
      </header>
      <main className={`page-body${narrow ? " narrow" : ""}`}>
        <h1>{title}</h1>
        {children}
      </main>
      <LegalFooter />
      <CheckoutHost />
      <TaskHost />
      <StoreThemeHost />
      <PlacementHost />
      <StoreSetupHost />
      <OfferSetupHost />
      <Toasts fixed />
    </div>
  );
}
